import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, Fragment } from 'react';
import type { QueryResult } from 'react-apollo';
import type { ApolloQueryResult } from 'apollo-client';
import { NetworkStatus } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { FeedType } from '../useFeedTypeSelector';
import { FETCH_MORE_PAGE_SIZE } from '../feedConstants';
import type { FeedPopularFeedQuery as FeedPopularFeedQueryType } from '../__types__/FeedPopularFeedQuery';

import { PopularFeedCard } from './PopularFeedCard';
import { FeedCardsWrapper } from './FeedCardsWrapper';
import { ExitFeedAllyButton } from './ExitFeedAllyButton';
import type { PopularFeedTimeGranularityValue } from '../Feed';

type Props = {
	popularFeedQuery: QueryResult<FeedPopularFeedQueryType>;
	handleFocusRef: () => void;
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
};

export const PopularFeedCards: FC<Props> = ({
	popularFeedQuery,
	handleFocusRef,
	popularFeedTimeGranularity,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const popularCardSetRef = useRef(new Set<string>());

	const { data, loading, error, networkStatus, fetchMore } = popularFeedQuery;
	const hasNextPage = data?.popularFeed?.pageInfo?.hasNextPage;
	const endCursor = data?.popularFeed?.pageInfo?.endCursor;
	const results = data?.popularFeed?.nodes || [];

	const loadNextPage = useCallback(async (): Promise<
		ApolloQueryResult<FeedPopularFeedQueryType> | undefined
	> => {
		try {
			if (!endCursor) {
				return undefined;
			}

			const newResult = await fetchMore({
				variables: {
					first: FETCH_MORE_PAGE_SIZE,
					after: endCursor,
					timeGranularity: popularFeedTimeGranularity,
				},
				updateQuery: (prevResults: FeedPopularFeedQueryType, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}
					fetchMoreResult.popularFeed.nodes = prevResults.popularFeed.nodes.concat(
						fetchMoreResult.popularFeed.nodes,
					);

					return fetchMoreResult;
				},
			});

			if (newResult.data) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'paginated',
						actionSubject: 'popularFeed',
						source: 'homeScreen',
						attributes: {
							resultCount: data
								? newResult.data.popularFeed.nodes.length - data?.popularFeed.nodes.length
								: -1,
						},
					},
				}).fire();
			}

			if (newResult.errors) {
				throw newResult.errors;
			}

			return newResult;
		} catch (e) {
			throw e;
		}
	}, [fetchMore, createAnalyticsEvent, endCursor, data, popularFeedTimeGranularity]);

	useEffect(() => {
		popularCardSetRef.current?.clear();
	});

	const disableAnimation = Boolean(
		process.env.REACT_SSR || (window.__SSR_RENDERED__ && networkStatus !== NetworkStatus.fetchMore),
	);

	return (
		<FeedCardsWrapper
			loading={loading}
			firstPageError={error}
			networkStatus={networkStatus}
			endCursor={endCursor}
			hasNextPage={hasNextPage}
			loadNextPage={loadNextPage}
			feedType={FeedType.POPULAR}
		>
			{results.map((result, index) => {
				if (
					result?.content?.labels?.nodes?.some(
						(value) => value?.name === 'farewell-blog' || value?.name === 'welcome-blog',
					) &&
					fg('confluence_welcome_farewell_feed')
				) {
					return null;
				}

				if (result.content.id && popularCardSetRef.current) {
					if (popularCardSetRef.current.has(result.content.id)) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'rendered',
								actionSubject: 'duplicate',
								actionSubjectId: 'popularFeedCard',
								source: 'homeScreen',
								attributes: {
									ranking: index,
									contentId: result.content.id,
								},
							},
						}).fire();
						return null;
					} else {
						popularCardSetRef.current.add(result.content.id);
					}
				}

				return (
					result && (
						<Fragment key={result.content.id}>
							<PopularFeedCard
								feedItem={result}
								ranking={index}
								disableAnimation={disableAnimation}
								timeGranularity={popularFeedTimeGranularity}
							/>
							<ExitFeedAllyButton
								onExit={handleFocusRef}
								source="popular-feed"
								feedCardIndex={index}
							/>
						</Fragment>
					)
				);
			})}
		</FeedCardsWrapper>
	);
};
